import AddPhotoAlternateRoundedIcon from "@mui/icons-material/AddPhotoAlternateRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import { Box, Button, IconButton, InputBase, Paper, Stack, Typography } from "@mui/material";
import type { KeyboardEvent, ReactNode, Ref } from "react";
import { useRef, useState } from "react";
import TooltipForDisabledAction from "@entrilia/shared/components/TooltipForDisabledAction";
import HiddenFileInput from "@entrilia/shared/components/inputs/HiddenFileInput";
import { makeTintedBackgroundFromColor } from "@entrilia/shared/utilities/colorHelper";

const acceptedImageTypes = ["image/*"];

interface Props {
  placeholder: string;
  attachments?: ReactNode;
  defaultValue?: string;
  inputRef?: Ref<HTMLTextAreaElement>;
  autoFocus?: boolean;
  isRunning: boolean;
  disabled: boolean;
  sendDisabled: boolean;
  attachDisabled: boolean;
  controls?: ReactNode;
  pickButton?: ReactNode;
  onSend: (message: string) => void;
  onStop: () => void;
  onFilesAdd: (files: File[]) => void;
}

const ArtifactAgentComposer = ({
  placeholder,
  attachments,
  defaultValue,
  inputRef,
  autoFocus,
  isRunning,
  disabled,
  sendDisabled,
  attachDisabled,
  controls,
  pickButton,
  onSend,
  onStop,
  onFilesAdd,
}: Props) => {
  const [value, setValue] = useState(defaultValue ?? "");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const trimmedValue = value.trim();
  const canSend = trimmedValue !== "" && !disabled && !sendDisabled;

  const handleSend = () => {
    if (!canSend) {
      return;
    }
    onSend(trimmedValue);
    setValue("");
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <Stack spacing={0.75}>
      <Paper
        variant="outlined"
        sx={(t) => ({
          bgcolor: disabled ? "background.grey" : undefined,
          "&:focus-within": {
            borderColor: "primary.main",
            boxShadow: `0 0 0 1px ${makeTintedBackgroundFromColor(t, t.palette.primary.main)}`,
          },
        })}
      >
        {attachments && <Box sx={{ px: 2, pt: 2 }}>{attachments}</Box>}
        <InputBase
          placeholder={placeholder}
          disabled={disabled}
          maxRows={10}
          multiline
          fullWidth
          autoFocus={autoFocus}
          inputRef={inputRef}
          data-gramm="false"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={handleKeyDown}
          slotProps={{ input: { "aria-label": "Message to the assistant" } }}
          sx={{ px: 2, pt: 1.5, pb: 1 }}
        />
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 1, pb: 1 }}>
          <Stack direction="row" sx={{ alignItems: "center" }}>
            <TooltipForDisabledAction title="Attach images" isActionDisabled={attachDisabled}>
              <IconButton
                aria-label="Attach images"
                disabled={attachDisabled}
                onClick={() => fileInputRef.current?.click()}
              >
                <AddPhotoAlternateRoundedIcon />
              </IconButton>
            </TooltipForDisabledAction>
            <HiddenFileInput
              ref={fileInputRef}
              acceptedFileTypes={acceptedImageTypes}
              multiple
              onFilesAdd={onFilesAdd}
            />
            {pickButton}
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            {controls}
            {isRunning ? (
              <Button variant="outlined" color="secondary" startIcon={<StopRoundedIcon />} onClick={onStop}>
                Stop
              </Button>
            ) : (
              <Button
                variant="contained"
                aria-label="Send message"
                disabled={!canSend}
                sx={{ minWidth: 0, p: 1 }}
                onClick={handleSend}
              >
                <ArrowUpwardRoundedIcon sx={{ fontSize: "1.125rem" }} />
              </Button>
            )}
          </Stack>
        </Box>
      </Paper>
      <Typography variant="caption" color="textSecondary" sx={{ px: 1 }}>
        Images are sent to the AI model.
      </Typography>
    </Stack>
  );
};

export default ArtifactAgentComposer;
